import {uploadDirectFile,contentFingerprint} from '../home_media_ui/direct';
// Транспорт обмера: маленькие запросы, подтверждённое смещение и отменяемые повторы.
export const INITIAL_CHUNK = 128 * 1024;
export const isRetryable = code => !code || [408, 425, 429].includes(code) || (code >= 500 && code !== 507);
const stopped = () => Object.assign(new Error('Загрузка приостановлена. Можно продолжить.'), { name: 'AbortError' });
export const checkSignal = signal => { if (signal?.aborted) throw stopped(); };
export function waitUpload(ms, signal) {
  return new Promise((resolve, reject) => {
    checkSignal(signal);
    const end = () => { clearTimeout(timer); signal?.removeEventListener('abort', abort); };
    const abort = () => { end(); reject(stopped()); };
    const timer = setTimeout(() => { end(); resolve(); }, ms);
    signal?.addEventListener('abort', abort, { once: true });
  });
}

export async function uploadJson(base, path, { token, data, signal, timeout = 60000 } = {}) {
  checkSignal(signal);
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(abort, timeout);
  try {
    const res = await fetch(`${base}/main/api/surveyor/${path}`, {
      method: data === undefined ? 'GET' : 'POST', signal: controller.signal, credentials: 'omit',
      headers: { Authorization: `Bearer ${token}`, ...(data === undefined ? {} : { 'Content-Type': 'application/json' }) },
      body: data === undefined ? undefined : JSON.stringify(data),
    });
    let body;
    try { body = await res.json(); } catch { return { status: 'error', code: res.status >= 400 ? res.status : 502, message: `Сервер вернул ответ HTTP ${res.status} вместо данных. Передайте менеджеру номер загрузки.` }; }
    return { ...body, code: res.status, message: body.message || (res.status === 401 ? 'Сессия истекла. Войдите в аккаунт и продолжите загрузку.' : body.detail) };
  } catch (error) {
    checkSignal(signal);
    return { status: 'error', code: 0, message: 'Сервер не ответил. Проверяем соединение…' };
  } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
}

export async function retryUploadRequest(fn, { signal, onRetry, attempts = 7 } = {}) {
  let result;
  for (let attempt = 0; attempt < attempts; attempt++) {
    checkSignal(signal);
    result = await fn();
    if (result?.status === 'ok' || !isRetryable(result?.code)) return result;
    if (attempt + 1 < attempts) {
      onRetry?.({ attempt: attempt + 1, message: result?.message });
      await waitUpload(Math.min(1000 * 2 ** attempt, 10000), signal);
    }
  }
  return result;
}

// ID известен ДО init, поэтому потерянный ответ не создаёт второй файл.
// Читаем файл частями, чтобы учитывать всё содержимое без загрузки видео целиком в память.
export async function surveyorFileId(processId, file, fileType, signal, onProgress) {
  return contentFingerprint(file, `${processId}:${fileType}`, signal, onProgress);
}

export function sendSurveyorChunk(base, uploadId, offset, blob, token, onProgress, signal) {
  return new Promise((resolve, reject) => {
    checkSignal(signal);
    const xhr = new XMLHttpRequest();
    let idle, settled = false;
    const cleanup = () => { clearTimeout(idle); signal?.removeEventListener('abort', abort); };
    const finish = result => { if (settled) return; settled = true; cleanup(); resolve(result); };
    const abort = () => { if (settled) return; settled = true; cleanup(); xhr.abort(); reject(stopped()); };
    const resetIdle = () => { clearTimeout(idle); idle = setTimeout(() => {
      finish({ status: 'error', code: 0, message: 'Соединение прервалось. Продолжаем с сохранённого места…' }); xhr.abort();
    }, 45000); };
    xhr.open('PUT', `${base}/main/api/surveyor/chunked-upload/${uploadId}/chunk/?offset=${encodeURIComponent(offset)}`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.timeout = 180000;
    xhr.upload.onprogress = e => { if (settled) return; resetIdle(); if (e.lengthComputable) onProgress?.({ loaded: Math.min(e.loaded, blob.size), total: blob.size }); };
    xhr.onload = () => {
      let data; try { data = JSON.parse(xhr.responseText); } catch { data = null; }
      finish({ status: xhr.status >= 200 && xhr.status < 300 && data?.status === 'ok' ? 'ok' :
        (xhr.status === 409 && data?.code === 'offset_mismatch' ? 'offset_mismatch' : 'error'),
        code: data ? xhr.status : (xhr.status >= 400 ? xhr.status : 502), data,
        message: (data?.message && data.message !== 'Ошибка' ? data.message : data?.detail) || `Сервер отклонил часть файла (HTTP ${xhr.status}). Передайте менеджеру номер загрузки.` });
    };
    xhr.onerror = () => finish({ status: 'error', code: 0, message: 'Ошибка соединения' });
    xhr.ontimeout = () => finish({ status: 'error', code: 0, message: 'Часть файла отправлялась слишком долго' });
    xhr.onabort = () => finish({ status: 'error', code: 0, message: 'Передача прервана' });
    signal?.addEventListener('abort', abort, { once: true });
    resetIdle();
    try { xhr.send(blob); } catch (error) { finish({ status: 'error', code: 0, message: error.message }); }
  });
}

// HOME_MEDIA_V2_TRANSPORT
export async function uploadSurveyorFile(base, { processId, file, fileType, token, onProgress, onRetry, signal, uploadId: explicitId, prepared }) {
  try {
    checkSignal(signal);
    const id = explicitId || await surveyorFileId(processId, file, fileType, signal);
    return await uploadDirectFile(base, {file, token, signal, onProgress, onRetry, prepared,
      initPath: '/main/api/surveyor/chunked-upload/init/',
      initData: {upload_id:id,process_id:processId,file_name:file.name,file_type:fileType,total_size:file.size,mime_type:file.type||''},
      completePath: `/main/api/surveyor/chunked-upload/${id}/complete/`});
  } catch(error) {
    if (error.name === 'AbortError') throw error;
    return {status:'error',code:error.status||0,message:error.message||'Загрузка приостановлена'};
  }
}
